import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { immer } from "zustand/middleware/immer";
import all_cpv_trees from "../Assets/CPVs/all_cpv_trees.json";
import default_mapdata from "../Assets/Mapdata/default_mapdata.json";
import { persist, createJSONStorage } from "zustand/middleware";
import i18n from "../i18n";
import idbKeyvalAdapter from "./1_utility";
import devDebug from "../Helpers/debug";

const defaultMapState = {
  level: 0,
  displayOption: "tender_count",
  refreshMap: true,
};

const initialState = {
  currentStep: 0,
  cpvTree: all_cpv_trees[i18n.language.slice(0, 2) || "en"],
  cpvs: [],
  cpvError: "",
  companyDescription: "",
  mapData: default_mapdata,
  mapState: defaultMapState,
  nuts: [],
  refreshLayer: false,
  above1000CPVs: false,
  stopRetries: false,
  checkboxCPVs: {},
  normalCPVs: {},
};
const currentAppVersionFromEnv = Number(import.meta.env.VITE_VERSION);

export const useRegisterStore = createWithEqualityFn(
  persist(
    immer((set, get) => ({
      ...initialState,
      // ------------------- INIT ----------------------
      setCurrentStep: (payload) =>
        set((state) => {
          state.currentStep = payload;
        }),
      // ------------------- CPVs --------------
      setCPVs: (payload) =>
        set((state) => {
          let convertedPayload = {};
          if (Array.isArray(payload)) {
            for (let cpv of payload) {
              convertedPayload[cpv] = true;
            }
            payload = convertedPayload;
          }

          state.cpvs = Object.keys(payload).filter((key) => !payload?.[key]?.partialChecked);
          let withPartialCheckedObj = {};
          let withoutPartialCheckedObj = {};
          let hasPartialAttribute = false;
          if (Object.keys(payload).length > 0 && payload[Object.keys(payload)[0]].hasOwnProperty("partialChecked")) {
            hasPartialAttribute = true;
          }
          if (hasPartialAttribute) {
            state.checkboxCPVs = payload;
            for (let cpv of Object.keys(payload)) {
              if (!payload[cpv].partialChecked) {
                withoutPartialCheckedObj[cpv] = true;
              }
            }
            state.normalCPVs = withoutPartialCheckedObj;
          } else {
            for (let cpv of Object.keys(payload)) {
              withPartialCheckedObj[cpv] = { checked: true, partialChecked: false };
            }
            state.checkboxCPVs = withPartialCheckedObj;
            state.normalCPVs = payload;
          }
        }),
      addCPV: (cpv) =>
        set((state) => {
          state.cpvs = [...state.cpvs, cpv];
          state.checkboxCPVs[cpv] = { checked: true, partialChecked: false };
          state.normalCPVs[cpv] = true;
        }),
      deleteCPV: (payload) =>
        set((state) => {
          state.cpvs = state.cpvs.filter((cpv) => cpv !== payload);
          delete state.checkboxCPVs[payload];
          delete state.normalCPVs[payload];
        }),
      setStopRetries: (stopRetries) =>
        set((state) => {
          state.stopRetries = stopRetries;
        }),
      setCPVError: (payload) =>
        set((state) => {
          state.cpvError = payload;
        }),
      setCompanyDescription: (payload) =>
        set((state) => {
          state.companyDescription = payload;
        }),
      setAbove1000CPVs: (payload) =>
        set((state) => {
          state.above1000CPVs = payload;
        }),
      // ------------------------ nuts map ---------------------
      setMetric: (payload) =>
        set((state) => {
          state.metric = payload;
        }),
      setDisplayOption: (payload) =>
        set((state) => {
          state.mapState.displayOption = payload;
        }),
      setLevel: (payload) =>
        set((state) => {
          state.mapState.level = payload;
        }),
      setRefreshMap: (payload) =>
        set((state) => {
          state.mapState.refreshMap = payload;
        }),
      setNUTS: (payload) =>
        set((state) => {
          state.nuts = payload;
        }),
      // ------------------- RESET ------------------------
      resetState: () => {
        useRegisterStore.persist.clearStorage();
        set(initialState);
      },
    })),
    {
      name: "registerSavedData",
      storage: createJSONStorage(() => idbKeyvalAdapter),
      version: currentAppVersionFromEnv,
      migrate: (persistedState, storedVersion) => {
        devDebug(
          `[registerStore Migrate] Called! Stored: ${storedVersion} (type: ${typeof storedVersion}), App: ${currentAppVersionFromEnv} (type: ${typeof currentAppVersionFromEnv})`
        );
        if (storedVersion !== currentAppVersionFromEnv) {
          devDebug('[registerStore Migrate] Versions differ. Resetting to initialState.');
          return initialState;
        }
        if (persistedState === null || persistedState === undefined) {
          devDebug('[registerStore Migrate] Persisted state is null/undefined despite version match. Resetting.');
          return initialState;
        }
        devDebug('[registerStore Migrate] Versions same. Using persisted state.');
        return persistedState;
      },
      // merge: (persistedState, currentState) => {
      //   devDebug('[registerStore Merge] Merging state...');
      //   if (typeof persistedState !== 'object' || persistedState === null) {
      //     devDebug('[registerStore Merge] Persisted state is not an object, returning current state or initialState.');
      //     return initialState;
      //   }
      //   return mergeDeepLeft(persistedState, currentState);
      // },
      onRehydrateStorage: (state) => {
        devDebug(`[registerStore onRehydrateStorage] Attempting rehydration...`);
        return (hydratedState, error) => {
          if (error) {
            console.error(
              `[registerStore] Failed to rehydrate store!`,
              error
            );
            devDebug(
              `[registerStore] Error during rehydration. Store name: "registerStore".`,
              "Error object:", error,
              "State passed to onRehydrateStorage:", state, // This is the raw value from storage before parsing/migration
              "Hydrated state (if available):", hydratedState // This is the state *after* potential migration/parsing, if it got that far
            );
          } else {
            // devDebug(
            //   `[registerStore] Successfully rehydrated. Hydrated state:`,
            //   hydratedState
            // );
          }
        };
      },
    }
  ),
  shallow
);
